/*
  Herfur accordion.

  Native <details> snaps open, which jumps everything below it down the page in
  one frame — that's the jolt. This animates the panel's height instead, and
  opens one row at a time so the page never grows by two panels at once.

  It stays a real <details>/<summary> underneath: keyboard operation, screen
  reader announcement and the no-JS fallback all come from the element itself.
  If the script never loads, the rows still open — just without the easing.

  Markup contract:
    [data-herfur-accordion-group]    wrapper; only one row inside it stays open
    [data-herfur-accordion]          the <details>
    [data-herfur-accordion-content]  the panel, with NO padding of its own
                                     (padding on an inner element, or height
                                     can never animate down to zero)
*/

(function () {
  var OPEN_MS = 260;
  var CLOSE_MS = 220;
  var EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function HerfurAccordion(details) {
    this.details = details;
    this.summary = details.querySelector('summary');
    this.content = details.querySelector('[data-herfur-accordion-content]');
    if (!this.summary || !this.content) return;

    this.animation = null;
    details.herfurAccordion = this;

    var self = this;
    this.summary.addEventListener('click', function (event) {
      // Take over the toggle so the open can be animated. Enter and Space on a
      // summary both dispatch click, so keyboard use comes along for free.
      event.preventDefault();
      self.toggle();
    });
  }

  HerfurAccordion.prototype.siblings = function () {
    var group = this.details.closest('[data-herfur-accordion-group]');
    if (!group) return [];

    var self = this;
    return Array.prototype.slice
      .call(group.querySelectorAll('[data-herfur-accordion]'))
      .filter(function (other) {
        return other !== self.details && other.open && other.herfurAccordion;
      });
  };

  HerfurAccordion.prototype.toggle = function () {
    if (this.details.open) {
      this.close();
    } else {
      this.open();
    }
  };

  HerfurAccordion.prototype.open = function () {
    var self = this;

    this.siblings().forEach(function (other) {
      other.herfurAccordion.close();
    });

    this.cancel();
    this.details.open = true;

    if (prefersReducedMotion()) return;

    // Measured after `open`, so the panel is laid out and the height is real
    var height = this.content.scrollHeight;

    this.content.style.overflow = 'hidden';
    this.animation = this.content.animate(
      [
        { height: '0px', opacity: 0 },
        { height: height + 'px', opacity: 1 },
      ],
      { duration: OPEN_MS, easing: EASING }
    );

    this.animation.onfinish = function () {
      self.settle();
    };
  };

  HerfurAccordion.prototype.close = function () {
    var self = this;
    this.cancel();

    if (prefersReducedMotion()) {
      this.details.open = false;
      return;
    }

    var height = this.content.scrollHeight;

    this.content.style.overflow = 'hidden';
    this.animation = this.content.animate(
      [
        { height: height + 'px', opacity: 1 },
        { height: '0px', opacity: 0 },
      ],
      { duration: CLOSE_MS, easing: EASING }
    );

    this.animation.onfinish = function () {
      self.details.open = false;
      self.settle();
    };
  };

  // Nothing is written to the element's inline height, so when the animation
  // ends the panel goes back to auto height on its own and reflows correctly.
  HerfurAccordion.prototype.settle = function () {
    this.content.style.overflow = '';
    this.animation = null;
  };

  HerfurAccordion.prototype.cancel = function () {
    if (!this.animation) return;
    this.animation.cancel();
    this.animation = null;
  };

  function initHerfurAccordions(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('[data-herfur-accordion]').forEach(function (details) {
      if (details.dataset.herfurAccordionReady === 'true') return;
      details.dataset.herfurAccordionReady = 'true';
      new HerfurAccordion(details);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initHerfurAccordions(document);
    });
  } else {
    initHerfurAccordions(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initHerfurAccordions(event.target);
  });
})();
